import React from 'react'
import { isEmpty } from 'lodash'

import Select from '@/components/forms/Select'
import { FormField } from '@/components/forms/FormField'
import { formatTime } from '@/utils/helpers'

// A save time is all that names a version, and every option is one — so the
// control sizes to its contents (autoWidth) rather than the default 190–300px
// band. Compact form, as other list views use, not the long
// "August 10, 2026 at 3:00 PM".
export const VERSION_TIME_FORMAT = 'MMM D, YYYY h:mm A'

// Every save cuts a new version, so a version is named by when it was saved.
const getVersionOptions = (versions) =>
  versions.map((version) => ({
    value: version.id,
    label: formatTime(version.created_at, VERSION_TIME_FORMAT),
    isCurrent: version.is_active,
  }))

// "(Current)" marks the live version in the open list only. On the closed
// control it's redundant — the read-only banner already says when you're on an
// older version — and it's the widest part of the label, which pushed the
// control past the edge of the page.
const formatVersionOption = (option, meta) =>
  meta.context === 'menu' && option.isCurrent ? `${option.label} (Current)` : option.label

/**
 * Picks which saved version of a Notetaker configuration the editor shows, from
 * that editor's section header. Older versions are read-only (see PromptForm
 * and OrgDefaultsForm) — picking one only changes what's on screen.
 *
 * Shown as soon as there's a history, even when that history is a single version:
 * the list is also the answer to "has this configuration ever been changed?", and
 * one entry says it hasn't been touched since it was created.
 *
 * Presentational: each editor owns its own history and what selecting a version
 * does, since the playbook flow and the org default flow load differently.
 *
 * @param {Array} props.versions The configuration's versions, newest first
 * @param {number} props.selectedId Id of the version currently on screen
 * @param {boolean} props.loading Whether the history is still loading
 * @param {Function} props.onSelect Called with the chosen version's id
 */
export const NotetakerVersionSelector = ({ versions = [], selectedId, loading, onSelect }) => {
  // Nothing to show until a history has loaded (it never does when the read
  // fails — see fetchBitFlowVersions).
  if (isEmpty(versions)) {
    return null
  }

  return (
    <FormField className="no-margin">
      <label>Version</label>
      <div data-testid="rtn-version-dropdown">
        <Select
          options={getVersionOptions(versions)}
          label="Version"
          value={selectedId}
          // Re-picking what's already on screen is a no-op, so a single-version
          // config can't reload itself and the current version can't be
          // "switched to" from itself.
          onChange={({ value }) => {
            if (value !== selectedId) {
              onSelect(value)
            }
          }}
          formatOptionLabel={formatVersionOption}
          isClearable={false}
          loading={loading}
          autoWidth
        />
      </div>
    </FormField>
  )
}
